import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import Layout from "./components/Layout";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const LinkRedirect = lazy(() => import("./pages/LinkRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LinksPaused = lazy(() => import("./pages/LinksPaused"));
const WatchAds = lazy(() => import("./pages/WatchAds"));
const LandingPageView = lazy(() => import("./pages/LandingPageView"));
const LandingPagesManager = lazy(() => import("./pages/LandingPagesManager"));
const LandingPageEditor = lazy(() => import("./pages/LandingPageEditor"));
const AnalyticsHistory = lazy(() => import("./pages/AnalyticsHistory"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <SubscriptionProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{width:'32px',height:'32px',border:'2px solid transparent',borderTopColor:'#8B5CF6',borderRadius:'50%',animation:'spin 1s linear infinite'}}></div><style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style></div>}>
            <Routes>
              {/* Content pages (AdSense-eligible) */}
              <Route path="/" element={<Layout><Index /></Layout>} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/contact" element={<Layout><Contact /></Layout>} />
              <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
              <Route path="/terms" element={<Layout><Terms /></Layout>} />
              <Route path="/blog" element={<Layout><BlogIndex /></Layout>} />
              <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
              
              {/* Utility pages (NO Google ads) */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/analytics/history" element={<AnalyticsHistory />} />
              <Route path="/landing-pages" element={<LandingPagesManager />} />
              <Route path="/landing-pages/edit/:id" element={<LandingPageEditor />} />
              <Route path="/watch-ads" element={<Layout><WatchAds /></Layout>} />
              <Route path="/links-paused" element={<Layout><LinksPaused /></Layout>} />
              <Route path="/l/:slug" element={<LinkRedirect />} />
              <Route path="/p/:slug" element={<LandingPageView />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </SubscriptionProvider>
  </QueryClientProvider>
);

export default App;
